import React from 'react';
import { Case, CaseStatus } from '../../../services/buildfire/rdb/cases';
import { IonItem, IonLabel, IonNote, IonRouterLink } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useFirebase } from '../../../services/firebase';
import { getFunctions } from 'firebase/functions';
import * as RDBCases from '../../../services/buildfire/rdb/cases';
import { getDatabase } from 'firebase/database';
import { formatCurrencyCents } from '../../../util/currency';
import { PaymentMethod } from '@stripe/stripe-js';
import { getPaymentMethods } from '../../../api/cloudFunctions';
import { CreditCardSummary } from '../../submitCase/components/paymentList/PaymentMethodListItem';
import styled from 'styled-components';

interface PaymentInfoProps {
  case: Case;
}

export const getPaymentStatusDescription = (
  c: Case,
  linkToParentAccount?: boolean
) => {
  if (c.invoiceAccount) {
    // invoiced case
    if (c.uid !== c.invoiceAccount.uid) {
      return (
        <>
          Invoiced to{' '}
          {linkToParentAccount ? (
            <IonRouterLink
              routerLink={`/admin/users/${c.invoiceAccount.uid}`}
              style={{ textDecoration: 'underline', marginLeft: '4px' }}
            >
              {c.invoiceAccount.displayName}
            </IonRouterLink>
          ) : (
            c.invoiceAccount.displayName
          )}
        </>
      );
    }
    return `Invoiced`;
  }

  // otherwise immediate payment
  if (c.status === CaseStatus.SIGNED_OFF) {
    return 'Paid';
  }
  return 'Payment pending';
};

interface PaymentInfo {
  invoice: RDBCases.Invoice | null;
  isLoading: boolean;
  paymentMethods: PaymentMethod[];
  stripeCustomerId: string;
}

export function usePaymentInfo(c: Case) {
  const { app } = useFirebase();
  const [paymentInfo, setPaymentInfo] = React.useState<PaymentInfo>({
    invoice: null,
    isLoading: true,
    paymentMethods: [],
    stripeCustomerId: '',
  });
  React.useEffect(() => {
    let cancelled = false;
    setPaymentInfo({
      invoice: null,
      paymentMethods: [],
      isLoading: true,
      stripeCustomerId: '',
    });

    const fetchInvoice = async () => {
      const db = getDatabase(app);
      const fns = getFunctions(app);
      try {
        const [invoice, paymentMethodsResult] = await Promise.all([
          RDBCases.getInvoice(db, c.id),
          getPaymentMethods(fns, {
            userId: c.uid,
            email: c.user.email,
            name: c.user.displayName,
          }),
        ]);

        if (cancelled) return;

        setPaymentInfo({
          invoice,
          isLoading: false,
          paymentMethods: paymentMethodsResult.paymentMethods,
          stripeCustomerId: paymentMethodsResult.stripeCustomerId,
        });
      } catch (e) {
        console.error(e);
        setPaymentInfo((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchInvoice();

    return () => {
      cancelled = true;
    };
  }, [c.id]);

  return paymentInfo;
}

const PaymentDescription = styled(IonNote)`
  display: flex;

  @media (max-width: 350px) {
    flex-direction: column;
  }
`;

const PaymentInfo: React.FC<PaymentInfoProps> = ({ case: c }) => {
  const history = useHistory();
  const isInvoiced = !!c.invoiceAccount;

  const { invoice, isLoading, paymentMethods } = usePaymentInfo(c);
  const totalCents =
    invoice?.products?.map((p) => p.amount)?.reduce((a, b) => a + b, 0) || 0;
  const total = formatCurrencyCents(totalCents);
  const selectedPaymentMethod = invoice
    ? paymentMethods.find((pm) => pm.id === invoice.paymentMethodId)
    : null;

  const cardInfo = selectedPaymentMethod?.card ? (
    <CreditCardSummary
      brand={selectedPaymentMethod.card.brand}
      lastFour={selectedPaymentMethod.card.last4}
    />
  ) : (
    'Error'
  );

  return (
    <IonItem
      button
      detail
      id="case-info_payment-info"
      onClick={(e) => {
        history.push(`${c.id}/edit-payment`, { from: 'caseInfo' });
      }}
    >
      <IonLabel>
        Payment Info
        <br />
        <PaymentDescription>
          {isLoading ? (
            'Loading...'
          ) : (
            <>
              {!isInvoiced && (
                <>
                  {cardInfo}
                  {' - '}
                </>
              )}
              {total} - {getPaymentStatusDescription(c)}
            </>
          )}
        </PaymentDescription>
      </IonLabel>
    </IonItem>
  );
};

export default PaymentInfo;
